import { useEffect, useRef } from "react";
import { CheckCircle2, Circle } from "lucide-react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";

/**
 * Host onboarding checklist — shows progress toward the Verified badge.
 * Fires confetti + toast the first time the host completes all 4 items.
 */
const LS_KEY = "ks_host_onboarding_complete";

export function HostOnboardingChecklist({ kitchens, bookings }) {
  const anyKitchen = kitchens.length > 0;
  const anyWithThreePhotos = kitchens.some((k) => (k.photos?.length || 0) >= 3);
  const anyWithAvailability = kitchens.some((k) => k.availability && Object.values(k.availability).some((v) => Array.isArray(v) && v.length > 0));
  const anyPaid = bookings.some((b) => b.payment_status === "paid");

  const items = [
    { key: "kitchen", label: "List your first kitchen", done: anyKitchen, cta: { to: "/list-your-kitchen", text: "Add a kitchen" } },
    { key: "photos",  label: "Add at least 3 photos to a listing", done: anyWithThreePhotos, cta: anyKitchen ? { to: `/kitchen/${kitchens[0].id}`, text: "Upload photos" } : null },
    { key: "windows", label: "Set weekly availability windows", done: anyWithAvailability, cta: anyKitchen ? { to: `/kitchen/${kitchens[0].id}`, text: "Set availability" } : null },
    { key: "paid",    label: "Earn your Verified badge on first paid booking", done: anyPaid },
  ];

  const doneCount = items.filter((i) => i.done).length;
  const total = items.length;
  const prevDoneRef = useRef(doneCount);

  useEffect(() => {
    const prev = prevDoneRef.current;
    prevDoneRef.current = doneCount;
    if (doneCount === total && prev < total) {
      let alreadyCelebrated = false;
      try { alreadyCelebrated = localStorage.getItem(LS_KEY) === "1"; } catch {}
      if (!alreadyCelebrated) {
        try { localStorage.setItem(LS_KEY, "1"); } catch {}
        confetti({ particleCount: 140, spread: 90, origin: { y: 0.35 }, colors: ["#FF3B30", "#ffffff", "#0a0a0a"] });
        setTimeout(() => confetti({ particleCount: 80, spread: 120, origin: { y: 0.4 }, colors: ["#FF3B30", "#ffffff"] }), 250);
        toast.success("You're a Verified host — nice work.");
      }
    }
  }, [doneCount, total]);

  if (doneCount === total) return null;

  return (
    <div data-testid="host-onboarding" className="mt-8 border hairline p-6">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="tick-label">Onboarding</div>
          <h2 className="font-display font-bold text-2xl mt-1">Get to a Verified host badge.</h2>
        </div>
        <div data-testid="onboarding-progress" className="font-mono text-sm text-neutral-400">{doneCount}/{total} done</div>
      </div>
      <ul className="mt-5 space-y-3">
        {items.map((i) => (
          <li key={i.key} data-testid={`onboard-${i.key}`} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {i.done ? (
                <CheckCircle2 size={18} className="text-[#FF3B30]" />
              ) : (
                <Circle size={18} className="text-neutral-600" />
              )}
              <span className={i.done ? "line-through text-neutral-500" : ""}>{i.label}</span>
            </div>
            {!i.done && i.cta && (
              <Link to={i.cta.to} data-testid={`onboard-cta-${i.key}`} className="btn-outline">{i.cta.text}</Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
